import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/security';
import LoginPage from './components/LoginPage';
import Navbar from './components/Navbar';
import ReleaseNotesHomePage from './components/ReleaseNotesHomePage';
import WikiHomePage from './components/WikiHomePage';
import WikiArticlePage from './components/WikiArticlePage';
import WikiEditorPage from './components/WikiEditorPage';
import WikiContentsPage from './components/WikiContentsPage';
import WikiBackupsPage from './components/WikiBackupsPage';
import WikiSectionPage from './components/WikiSectionPage';
import AdminPage from './components/AdminPage';
import NoAccessPage from './components/NoAccessPage';
import SearchResultsPage from './components/SearchResultsPage';

import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { msalInstance } from './lib/auth';
import { AuthProvider } from './lib/security';
import {
    buildNumber,
    shortCommitSha,
    commitUrl,
    environmentName,
    formattedBuildTime,
    isLocalBuild,
} from './utils/buildInfo';

function MainApp() {
  const { isAuthenticated, isLoading, accessStatus, hasAnyAccess, canRead, canWrite, canAdmin, isSystemAdmin } = useAuth();

  if (isLoading) {
      return (
          <div className="flex items-center justify-center min-h-screen bg-slate-50">
              <div className="text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-slate-500">Loading BIKI...</p>
              </div>
          </div>
      );
  }

  // Access decides what renders, so nothing renders until it is known. Without
  // this the app briefly draws with no entitlements and flashes the no-access
  // screen at people who do have access.
  if (isAuthenticated && accessStatus === 'loading') {
      return (
          <div className="flex items-center justify-center min-h-screen bg-slate-50">
              <div className="text-center">
                  <div className="spinner mx-auto mb-4"></div>
                  <p className="text-slate-500">Checking your access...</p>
              </div>
          </div>
      );
  }

  // Could not find out. Say that, rather than reporting it as a refusal.
  if (isAuthenticated && accessStatus === 'error') {
      return (
          <div className="min-h-screen flex flex-col bg-slate-50">
              <NoAccessPage variant="error" />
          </div>
      );
  }

  // Authenticated but entitled to nothing: say so, rather than showing an app
  // whose every request would be refused.
  if (isAuthenticated && !hasAnyAccess) {
      return (
          <div className="min-h-screen flex flex-col bg-slate-50">
              <NoAccessPage />
          </div>
      );
  }

  const home = canRead('releaseNotes') ? '/releases' : canRead('wiki') ? '/wiki' : '/admin';

  return (
      <div className="min-h-screen flex flex-col bg-slate-50">
          {isAuthenticated && <Navbar />}
          <main className="flex-1">
              <Routes>
                  {/* Auth routes */}
                  <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" />} />

                  {/* Landing on the first area this person can actually reach.
                      A wiki-only reader must not be bounced to release notes. */}
                  <Route path="/" element={isAuthenticated ? <Navigate to={home} /> : <LoginPage />} />

                  {/* Release Notes routes */}
                  <Route path="/releases" element={canRead('releaseNotes') ? <ReleaseNotesHomePage /> : <Navigate to={home} />} />
                  {/* /releases/BIF-87 opens that note. The same component: it
                      resolves the key, then selects the release and the note,
                      so a link works without knowing either id. */}
                  <Route path="/releases/:jiraKey" element={canRead('releaseNotes') ? <ReleaseNotesHomePage /> : <Navigate to={home} />} />

                  {/* Wiki routes */}
                  <Route path="/wiki" element={canRead('wiki') ? <WikiHomePage /> : <Navigate to={home} />} />
                  <Route path="/wiki/section/:tocId" element={canRead('wiki') ? <WikiSectionPage /> : <Navigate to={home} />} />
                  <Route path="/wiki/article/:id" element={canRead('wiki') ? <WikiArticlePage /> : <Navigate to={home} />} />
                  <Route path="/wiki/edit/:id?" element={canWrite('wiki') ? <WikiEditorPage /> : <Navigate to={home} />} />
                  <Route path="/wiki/contents" element={canAdmin('wiki') ? <WikiContentsPage /> : <Navigate to={home} />} />
                  {/* Backups need Admin of the wiki, not system administration,
                      so they live here beside Manage Contents rather than on /admin. */}
                  <Route path="/wiki/backups" element={canAdmin('wiki') ? <WikiBackupsPage /> : <Navigate to={home} />} />

                  {/* Search route */}
                  <Route path="/search/:query" element={<SearchResultsPage />} />

                  {/* Admin routes */}
                  <Route path="/admin" element={isSystemAdmin ? <AdminPage /> : <Navigate to={home} />} />

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to={isAuthenticated ? home : "/login"} />} />
              </Routes>
          </main>
          <footer className="no-print py-4 text-center text-sm text-slate-400 border-t border-slate-200">
              <p>BIKI - Release Notes &amp; Documentation Platform</p>
              <p className="mt-1 text-xs text-slate-400">
                  <span title={`Built ${formattedBuildTime()}`}>
                      {isLocalBuild ? 'Local build' : `Build ${buildNumber}`}
                  </span>
                  {shortCommitSha && (
                      <>
                          <span className="mx-1.5 text-slate-300">&middot;</span>
                          {commitUrl ? (
                              <a
                                  href={commitUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono hover:text-slate-600 hover:underline"
                                  title="View this commit in Azure DevOps"
                              >
                                  {shortCommitSha}
                              </a>
                          ) : (
                              <span className="font-mono">{shortCommitSha}</span>
                          )}
                      </>
                  )}
                  {/* Only badge deployed non-production environments; a local
                      build already says so. */}
                  {!isLocalBuild && environmentName !== 'Production' && (
                      <>
                          <span className="mx-1.5 text-slate-300">&middot;</span>
                          <span className="font-medium text-amber-600">{environmentName}</span>
                      </>
                  )}
              </p>
          </footer>
      </div>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <AuthProvider>
          <MainApp />
        </AuthProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
